import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";

export default function Signup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business: "", phone: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { name: form.name, email: form.email, password: form.password };
      if (form.business) payload.business = form.business;
      if (form.phone) payload.phone = form.phone;
      const { data } = await api.post("/auth/signup", payload);
      if (data.access_token) {
        localStorage.setItem("sz_token", data.access_token);
        setUser(data.user);
        toast.success("Welcome to ShipZilla — account synced with the app.");
        navigate(data.user?.role === "admin" ? "/admin" : "/dashboard");
      } else {
        toast.success("Account created — now log in.");
        navigate("/login");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell quote={<>SELL BIG.<br />SHIP <span className="text-sky-300">BIGGER.</span></>} author="— 27,000+ pincodes covered">
      <div data-testid="signup-page">
        <h1 className="font-display font-black tracking-tight text-4xl">Sign Up Free</h1>
        <p className="text-sm text-zinc-500 mt-2">One account for the app and the web. 3 minutes, no credit card.</p>
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="signup-form">
          {error && (
            <div data-testid="signup-error-alert" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">
              {error}
            </div>
          )}
          {[
            { id: "signup-name", key: "name", label: "Full name", type: "text", ph: "Aarav Sharma", req: true },
            { id: "signup-business", key: "business", label: "Business / Brand", type: "text", ph: "Zilla Traders", req: false },
            { id: "signup-phone", key: "phone", label: "Mobile (10 digits)", type: "tel", ph: "9876543210", req: false },
            { id: "signup-email", key: "email", label: "Email", type: "email", ph: "you@brand.in", req: true },
            { id: "signup-password", key: "password", label: "Password (min 6 chars)", type: "password", ph: "••••••••", req: true },
          ].map((f) => (
            <div key={f.key}>
              <label htmlFor={f.id} className="text-xs font-mono font-bold uppercase tracking-[0.2em]">{f.label}</label>
              <input
                id={f.id}
                data-testid={`${f.id}-input`}
                type={f.type}
                required={f.req}
                value={form[f.key]}
                onChange={set(f.key)}
                placeholder={f.ph}
                className="mt-2 w-full border-2 border-obsidian bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
              />
            </div>
          ))}
          <button
            type="submit"
            data-testid="signup-submit-button"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-obsidian text-sky-300 font-bold px-6 py-4 border-2 border-obsidian shadow-[4px_4px_0px_0px_rgba(10,10,10,0.35)] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create Account"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
        <p className="text-sm text-zinc-500 mt-8">
          Already registered on the app?{" "}
          <Link to="/login" data-testid="signup-login-link" className="font-bold text-obsidian underline decoration-acid decoration-4 underline-offset-4">
            Login here
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
